import { RoleEnum } from '@zeus/index'
import { createBrowserRouter, redirect } from 'react-router-dom'
import { GQL_ME } from '~/gql/auth/auth'
import { client } from '~/helpers/apollo'
import { useAuth } from '~/helpers/store/auth'
import App from '~/pages/App'
import ConfirmTrial from '~/pages/authentication/ConfirmTrial'
import CreateAccount from '~/pages/authentication/CreateAccount'
import Denied from '~/pages/authentication/Denied'
import Login from '~/pages/authentication/Login'
import Logout from '~/pages/authentication/Logout'
import MagicLink from '~/pages/authentication/MagicLink'
import NewPassword from '~/pages/authentication/NewPassword'
import ResetPassword from '~/pages/authentication/ResetPassword'
import Signup from '~/pages/authentication/Signup'
import SsoCallback from '~/pages/authentication/SsoCallback'
import ValidAccount from '~/pages/authentication/ValidAccount'
import BillingPage from '~/pages/backoffice/admin/billing/BillingPage'
import BrandingSettings from '~/pages/backoffice/admin/branding/BrandingSettings'
import CertificateSettings from '~/pages/backoffice/admin/certificates/CertificateSettings'
import GroupsTable from '~/pages/backoffice/admin/groups/GroupsTable'
import HomePage from '~/pages/backoffice/admin/home/HomePage'
import ModulesTable from '~/pages/backoffice/admin/modules/ModulesTable'
import OrganizationsTable from '~/pages/backoffice/admin/organizations/OrganizationsTable'
import TrialRequests from '~/pages/backoffice/admin/organizations/TrialRequests'
import ReminderSettings from '~/pages/backoffice/admin/reminders/ReminderSettings'
import RoadmapPage from '~/pages/backoffice/admin/roadmap/RoadmapPage'
import SessionsTable from '~/pages/backoffice/admin/sessions/SessionsTable'
import SsoSettings from '~/pages/backoffice/admin/sso/SsoSettings'
import StatisticsTable from '~/pages/backoffice/admin/statistics/StatisticsTable'
import UserTable from '~/pages/backoffice/admin/user/UserTable'
import Error from '~/pages/errors/Error'
import NotFound from '~/pages/errors/NotFound'
import BackOfficeLayout from '~/pages/layouts/BackOfficeLayout'
import Root from '~/pages/layouts/Root'
import MySessions from '~/pages/scorm/MySessions'
import ScormModulePreview from '~/pages/scorm/ScormModulePreview'
import ScormPlayer from '~/pages/scorm/ScormPlayer'
import ScormResume from '~/pages/scorm/ScormResume'
import { trackNavigation } from '~/tracing'

export type UserData = {
  firstName: string
  lastName: string
  email: string
  role: RoleEnum
  id: string
} | null

export function getDefaultRoute(role: RoleEnum): string {
  switch (role) {
    case 'SuperAdmin' as RoleEnum:
      return '/back-office/organizations'
    // Admin comme formateur atterrissent sur l'accueil (#201) et non sur un
    // outil : arrivé le matin, on se demande ce qu'on a à faire, pas comment
    // interpréter un tableau. Le SuperAdmin en est exclu — opérateur
    // plateforme, il n'appartient à aucune organisation et n'a donc ni promo
    // ni parcours à suivre.
    case 'Admin' as RoleEnum:
    case 'Formateur' as RoleEnum:
      return '/back-office/home'
    case 'User' as RoleEnum:
      return '/my-sessions'
    default:
      return '/'
  }
}

export function createProtectedLoader(allowedRoles: RoleEnum[]) {
  return async () => {
    if (allowedRoles.length === 0) {
      useAuth.getState().setLoading(false)
      return null
    }

    const currentState = useAuth.getState()

    if (currentState.payload) {
      const userRole = currentState.payload.role as RoleEnum

      if (!allowedRoles.includes(userRole)) {
        return redirect(getDefaultRoute(userRole))
      }

      return null
    }

    try {
      const { data } = await client.query({
        query: GQL_ME,
        fetchPolicy: 'network-only',
      })

      if (data?.me) {
        const userRole = data.me.roles as RoleEnum

        useAuth.getState().setPayload({
          firstName: data.me.firstName,
          lastName: data.me.lastName,
          email: data.me.email,
          role: userRole,
          id: data.me.id,
          impersonatorId: data.me.impersonatorId,
        })

        if (!allowedRoles.includes(userRole)) {
          return redirect(getDefaultRoute(userRole))
        }

        return null
      } else {
        useAuth.getState().clearAuth()
        return redirect(`/`)
      }
    } catch (_error) {
      useAuth.getState().clearAuth()
      return redirect(`/`)
    }
  }
}

// URL par organisation (#50) : toutes les routes vivent sous /o/:orgSlug via le
// basename react-router. Les chemins absolus internes (navigate, Link) sont donc
// automatiquement préfixés — aucune adaptation de la navigation nécessaire.
const orgMatch = window.location.pathname.match(/^\/o\/([^/]+)/)
if (!orgMatch) {
  // Pas d'org dans l'URL → redirige vers l'org par défaut en préservant le chemin.
  const suffix = window.location.pathname === '/' ? '' : window.location.pathname
  window.location.replace(`/o/default${suffix}${window.location.search}`)
}
const orgBasename = `/o/${orgMatch?.[1] ?? 'default'}`

export const router = createBrowserRouter([
  {
    path: '/',
    Component: Root,
    errorElement: <Error />,
    children: [
      {
        index: true,
        Component: App,
        loader: createProtectedLoader([]),
      },
      { path: 'logout', Component: Logout },
      { path: 'reset-password', Component: ResetPassword },
      { path: 'valid-account', Component: ValidAccount },
      { path: 'new-password', Component: NewPassword },
      { path: 'create-account', Component: CreateAccount },
      { path: 'magic-link', Component: MagicLink },
      { path: 'sso', Component: SsoCallback },
      { path: 'denied', Component: Denied },
      { path: 'login', Component: Login },
      // Parcours d'essai en libre-service (#155) — publiques, sans loader.
      { path: 'signup', Component: Signup },
      { path: 'confirm-trial', Component: ConfirmTrial },
      {
        path: 'my-sessions',
        Component: MySessions,
        loader: createProtectedLoader([RoleEnum.Admin, RoleEnum.Formateur, RoleEnum.User]),
      },
      {
        path: 'scorm/preview/:moduleId',
        Component: ScormModulePreview,
        loader: createProtectedLoader([RoleEnum.Admin, RoleEnum.Formateur]),
      },
      // ⚠️ La route SANS module est conservée, et ce n'est pas de la
      // complaisance : c'est celle que portent les liens de rappel par mail
      // (`reminders.service.ts`), déjà partis dans des boîtes. Elle redirige
      // vers le prochain module ouvert et non terminé, ce qui est exactement le
      // sens voulu — « reprends où tu en étais » (ADR 0002 § 7.3).
      {
        path: 'scorm/:sessionId',
        Component: ScormResume,
        loader: createProtectedLoader([RoleEnum.Admin, RoleEnum.Formateur, RoleEnum.User]),
      },
      {
        path: 'scorm/:sessionId/:moduleId',
        Component: ScormPlayer,
        loader: createProtectedLoader([RoleEnum.Admin, RoleEnum.Formateur, RoleEnum.User]),
      },
      {
        path: 'back-office',
        Component: BackOfficeLayout,
        children: [
          {
            path: 'home',
            Component: HomePage,
            loader: createProtectedLoader([RoleEnum.Admin, RoleEnum.Formateur]),
          },
          {
            path: 'statistics',
            Component: StatisticsTable,
            loader: createProtectedLoader([RoleEnum.Admin]),
          },
          {
            path: 'modules',
            Component: ModulesTable,
            loader: createProtectedLoader([RoleEnum.Admin, RoleEnum.Formateur]),
          },
          {
            path: 'sessions',
            Component: SessionsTable,
            loader: createProtectedLoader([RoleEnum.Admin, RoleEnum.Formateur]),
          },
          {
            path: 'users',
            Component: UserTable,
            loader: createProtectedLoader([RoleEnum.Admin]),
          },
          {
            path: 'groups',
            Component: GroupsTable,
            loader: createProtectedLoader([RoleEnum.Admin]),
          },
          {
            path: 'certificates',
            Component: CertificateSettings,
            loader: createProtectedLoader([RoleEnum.Admin]),
          },
          {
            path: 'reminders',
            Component: ReminderSettings,
            loader: createProtectedLoader([RoleEnum.Admin]),
          },
          {
            path: 'settings',
            Component: BrandingSettings,
            loader: createProtectedLoader([RoleEnum.Admin]),
          },
          {
            path: 'sso',
            Component: SsoSettings,
            loader: createProtectedLoader([RoleEnum.Admin]),
          },
          {
            path: 'roadmap',
            Component: RoadmapPage,
            loader: createProtectedLoader([RoleEnum.Admin]),
          },
          {
            path: 'organizations',
            Component: OrganizationsTable,
            loader: createProtectedLoader([RoleEnum.SuperAdmin]),
          },
          {
            path: 'trial-requests',
            Component: TrialRequests,
            loader: createProtectedLoader([RoleEnum.SuperAdmin]),
          },
          {
            path: 'billing',
            Component: BillingPage,
            loader: createProtectedLoader([RoleEnum.SuperAdmin, RoleEnum.Admin]),
          },
        ],
      },
      { path: 'error', Component: Error, loader: createProtectedLoader([]) },
      { path: '*', Component: NotFound, loader: createProtectedLoader([]) },
    ],
  },
], { basename: orgBasename })

let lastTrackedKey = ''
router.subscribe((state) => {
  if (state.navigation.state !== 'idle') return
  const { pathname, search } = state.location
  const key = `${pathname}${search}`
  if (key === lastTrackedKey) return
  lastTrackedKey = key
  trackNavigation(pathname, search)
})
